import { useNavigate } from "@remix-run/react";
import type { Dispatch } from "react";
import { useRef, useState } from "react";
import useTranslation from "~/kernel/internationalization/useTranslation";
import ROUTES from "~/kernel/routing/ROUTES";
import useCompanySearch from "~/company/helpers/useCompanySearch";
import LinkList from "~/ui/LinkList";
import Modal from "~/ui/Modal";
import SearchInput from "~/ui/SearchInput";
import type { TCompanyListItem } from "../companies._index/TCompanyListItem";
import fullAddress from "~/kernel/adress/fullAddress";

export default function PhoneSearchInput() {
  const { getScopedT, tt } = useTranslation("index");
  const scopedT = getScopedT("hero");
  const [query, setQuery] = useState("");
  const [showModal, setShowModal] = useState(false);
  const navigate = useNavigate();
  const navigateToResults = () => navigate(ROUTES.companies.path(query));

  return (
    <>
      <div className="flex flex-row">
        <input
          id="search-company"
          className="bg-white rounded-l-md xl:text-xl w-full py-3 px-5 border border-slate-200 border-r-0"
          placeholder={scopedT("searchPlaceholder")}
          aria-label={scopedT("searchPlaceholder")}
          onChange={(ev) => {
            setQuery(ev.target.value);
          }}
          onClick={(ev) => {
            setShowModal(true);
            ev.currentTarget.blur();
          }}
        />
        <button
          className="rounded-r-md bg-primary text-white py-3 px-5 whitespace-nowrap xl:text-xl hover:opacity-80 border-pink-600 border"
          onClick={navigateToResults}
        >
          <span>{tt("bookNow")}</span>
        </button>
      </div>
      <SearchModal
        query={query}
        setQuery={setQuery}
        show={showModal}
        setShow={setShowModal}
      />
    </>
  );
}

type SearchModalProps = {
  query: string;
  setQuery: Dispatch<string>;
  show: boolean;
  setShow: Dispatch<boolean>;
};

function SearchModal({ show, setShow, query, setQuery }: SearchModalProps) {
  const ref = useRef<HTMLInputElement>(null);
  const { getScopedT } = useTranslation();
  const scopedT = getScopedT("hero");
  const fetcher = useCompanySearch(query);
  const companies = fetcher.data?.companies ?? [];
  const suggestionCount = 5;
  const suggestedCompanies = companies.slice(0, suggestionCount);

  return (
    <Modal
      show={show}
      onCancel={() => {
        setShow(false);
        if (ref.current) {
          ref.current.blur();
        }
      }}
    >
      <SearchInput
        ref={ref}
        autoFocus
        id="query"
        placeholder={scopedT("searchPlaceholder")}
        value={query}
        onChange={(ev) => setQuery(ev.target.value)}
      />
      <LinkList
        links={suggestedCompanies.map((company) => ({
          to: ROUTES.company.path({ params: { slug: company.slug } }),
          children: <CompanySearchSuggestion company={company} />,
        }))}
      />
    </Modal>
  );
}

type CompanySearchSuggestionProps = {
  company: TCompanyListItem;
};

function CompanySearchSuggestion({ company }: CompanySearchSuggestionProps) {
  return (
    <div className="justify-center gap-3 md:grid md:grid-cols-[minmax(0,2fr)_minmax(0,8fr)]">
      <div className="hidden md:block">
        <img
          className="w-full rounded-md border border-slate-100"
          src={company.bannerUrl}
          alt={`Banner of ${company.name}`}
        />
      </div>
      <div>
        <p>{company.name}</p>
        <p className="text-xs">{fullAddress(company)}</p>
      </div>
    </div>
  );
}
